import { useCallback, useEffect, useRef, useState } from 'react'
import { authorizedFetch, API_ORIGIN } from '@yeshuman/sdk'
import { toast } from 'sonner'

/** How long the in-app “Taking you to …” status stays visible after opening the board tab. */
export const MARKETPLACE_APPLY_REDIRECT_STATUS_MS = 7000

export type MarketplaceOutboundApplyJob = {
  id: string
  redirect_url?: string | null
}

export type UseMarketplaceOutboundApplyOptions = {
  job: MarketplaceOutboundApplyJob | null | undefined
  /** Short board label shown in Apply / Taking you to … copy (Jooble, CareerJet, …). */
  boardName: string
  note: string
  /** Alpha capture: record interest only; do not open the provider tab. */
  captureApply: boolean
  expired?: boolean
  onAlphaCaptured?: () => void
}

export type MarketplaceOutboundApplyApi = {
  interestSaved: boolean
  submitting: boolean
  redirecting: boolean
  /** Primary Apply busy (submitting or redirecting status). */
  busy: boolean
  /** Label for the primary Apply control. */
  applyLabel: string
  /** Show external-link affordance when not in the redirecting status. */
  applyExternal: boolean
  applyDisabled: boolean
  /** Express interest only (secondary control). */
  expressInterest: () => Promise<boolean>
  /** Primary Apply: open board ASAP, record interest + outbound click in background. */
  applyViaBoard: () => Promise<void>
  /** Short status line while the provider tab is opening. */
  redirectStatus: string | null
  /**
   * When the browser blocked `window.open`, the apply URL for a visible
   * same-tab / manual open fallback (cleared on the next successful open).
   */
  blockedOpenUrl: string | null
  /** Navigate to the board in this tab (popup-blocked fallback). */
  openBoardInSameTab: () => void
}

/**
 * Open a provider board tab during the user gesture.
 *
 * Do not use the `noopener` feature string: browsers then return `null` even when
 * the tab opens, so callers cannot detect a real popup block. Blank-tab first
 * keeps the open synchronous and lets us assign `location` after checking the handle.
 */
export function openProviderBoardTab(url: string): Window | null {
  const trimmed = (url || '').trim()
  if (!trimmed || typeof window === 'undefined') return null

  const popup = window.open('about:blank', '_blank')
  if (!popup) return null

  try {
    popup.opener = null
  } catch {
    // Some browsers freeze opener; ignore.
  }
  try {
    popup.location.href = trimmed
  } catch {
    try {
      popup.location.assign(trimmed)
    } catch {
      popup.close()
      return null
    }
  }
  return popup
}

/**
 * Shared marketplace Apply flow for every listing provider.
 *
 * Opens the provider tab during the user gesture (avoids popup blockers and the
 * wait from serial interest → outbound-click awaits), then records interest
 * and outbound click with `keepalive` in the background. If the popup is blocked,
 * exposes a visible Open {board} link / same-tab navigation.
 */
export function useMarketplaceOutboundApply(
  options: UseMarketplaceOutboundApplyOptions,
): MarketplaceOutboundApplyApi {
  const {
    job,
    boardName,
    note,
    captureApply,
    expired = false,
    onAlphaCaptured,
  } = options

  const [interestSaved, setInterestSaved] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [redirecting, setRedirecting] = useState(false)
  const [blockedOpenUrl, setBlockedOpenUrl] = useState<string | null>(null)
  const redirectTimerRef = useRef<number | null>(null)

  const board = (boardName || 'board').trim() || 'board'
  const redirectUrl = (job?.redirect_url || '').trim() || null

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current != null) {
        window.clearTimeout(redirectTimerRef.current)
      }
    }
  }, [])

  const beginRedirectStatus = useCallback(() => {
    setRedirecting(true)
    if (redirectTimerRef.current != null) {
      window.clearTimeout(redirectTimerRef.current)
    }
    redirectTimerRef.current = window.setTimeout(() => {
      setRedirecting(false)
      redirectTimerRef.current = null
    }, MARKETPLACE_APPLY_REDIRECT_STATUS_MS)
  }, [])

  const expressInterest = useCallback(
    async (opts?: { trackSubmitting?: boolean }): Promise<boolean> => {
      if (!job) return false
      const trackSubmitting = opts?.trackSubmitting !== false
      if (trackSubmitting) setSubmitting(true)
      try {
        const response = await authorizedFetch(
          `${API_ORIGIN}/api/opportunities/marketplace/${encodeURIComponent(job.id)}/interest`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note }),
            keepalive: true,
          },
        )
        if (response.ok) {
          setInterestSaved(true)
          return true
        }
        const errorBody = await response.json().catch(() => ({}))
        toast.error((errorBody as { error?: string }).error || 'Could not save your interest')
        return false
      } finally {
        if (trackSubmitting) setSubmitting(false)
      }
    },
    [job, note],
  )

  const recordOutboundClick = useCallback(async (): Promise<boolean> => {
    if (!job) return false
    const response = await authorizedFetch(
      `${API_ORIGIN}/api/opportunities/marketplace/${encodeURIComponent(job.id)}/outbound-click`,
      {
        method: 'POST',
        keepalive: true,
      },
    )
    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}))
      toast.error(
        (errorBody as { error?: string }).error || 'Could not record apply click',
      )
      return false
    }
    return true
  }, [job])

  const openBoardInSameTab = useCallback(() => {
    const url = blockedOpenUrl || redirectUrl
    if (!url) return
    window.location.assign(url)
  }, [blockedOpenUrl, redirectUrl])

  const applyViaBoard = useCallback(async () => {
    if (!job || expired || redirecting) return

    if (captureApply) {
      const interested = await expressInterest()
      if (interested) onAlphaCaptured?.()
      return
    }

    if (!redirectUrl) {
      toast.error('This listing has no apply link yet')
      return
    }

    // Open during the user gesture — do not await network first.
    beginRedirectStatus()
    const popup = openProviderBoardTab(redirectUrl)
    if (!popup) {
      setBlockedOpenUrl(redirectUrl)
      setRedirecting(false)
      if (redirectTimerRef.current != null) {
        window.clearTimeout(redirectTimerRef.current)
        redirectTimerRef.current = null
      }
      toast.message(`Popup blocked — use Open ${board} below, or continue in this tab.`)
    } else {
      setBlockedOpenUrl(null)
    }

    // Interest must land before outbound-click (API gate); both are non-blocking for UX.
    void (async () => {
      const interested = await expressInterest({ trackSubmitting: false })
      if (!interested) return
      await recordOutboundClick()
    })()
  }, [
    job,
    expired,
    redirecting,
    captureApply,
    redirectUrl,
    board,
    expressInterest,
    recordOutboundClick,
    beginRedirectStatus,
    onAlphaCaptured,
  ])

  const applyLabel = redirecting
    ? `Taking you to ${board}…`
    : `Apply via ${board}`

  const redirectStatus = redirecting
    ? `Taking you to ${board}… the new tab can take a few seconds to load.`
    : blockedOpenUrl
      ? `Your browser blocked the new tab. Open ${board} below, or continue in this tab.`
      : null

  return {
    interestSaved,
    submitting,
    redirecting,
    busy: submitting || redirecting,
    applyLabel,
    applyExternal: !captureApply && !redirecting,
    applyDisabled: expired || redirecting || (!captureApply && !redirectUrl),
    expressInterest: () => expressInterest(),
    applyViaBoard,
    redirectStatus,
    blockedOpenUrl,
    openBoardInSameTab,
  }
}
